import { formatCurrency, formatPercentage, formatYears } from '../utils/formatters';

/**
 * Print Service for Solar Financial Model
 * Handles PDF generation for all tabs with proper formatting
 */

export const generatePrintableReport = async (inputs, outputs, tabs) => {
    const printableTabs = tabs.filter(tab => tab !== 'README');
    
    let allTabsContent = '';
    
    // Generate content for each tab
    for (let i = 0; i < printableTabs.length; i++) {
        const tab = printableTabs[i];
        try {
            let tabHtml = '';
            
            // Render tab content without Explanation components
            switch (tab) {
                case 'Summary':
                    tabHtml = generateSummaryTabHtml(inputs, outputs);
                    break;

                case 'Basic Data':
                    tabHtml = generateBasicDataTabHtml(inputs);
                    break;

                case 'Investment & Financing':
                    tabHtml = generateInvestmentFinancingTabHtml(outputs);
                    break;

                case 'Loan Repayment':
                    tabHtml = generateLoanRepaymentTabHtml(outputs);
                    break;

                case 'Total Cost':
                    tabHtml = generateTotalCostTabHtml(outputs);
                    break;

                case 'Profit & Loss':
                    tabHtml = generateProfitLossTabHtml(outputs);
                    break;

                case 'Balance Sheet':
                    tabHtml = generateBalanceSheetTabHtml(outputs);
                    break;

                case 'EVA':
                    tabHtml = generateEVATabHtml(outputs);
                    break;

                case 'Charts':
                    tabHtml = generateChartsTabHtml(inputs, outputs);
                    break;

                default:
                    tabHtml = generateDefaultTabHtml(tab);
                    break;
            }
            
            allTabsContent += tabHtml;
        } catch (error) {
            console.error(`Error rendering ${tab}:`, error);
            allTabsContent += generateErrorTabHtml(tab);
        }
    }

    return allTabsContent;
};

const generateSummaryTabHtml = (inputs, outputs) => {
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - Summary</h1>
            <div class="summary-print-container">
                <h2 class="text-2xl font-bold text-gray-800 mb-4">Financial Indicators Summary</h2>
                <div class="grid md:grid-cols-2 gap-6">
                    <div class="bg-white p-4 rounded-lg border border-gray-200">
                        <h3 class="font-semibold text-lg mb-3 text-blue-700">Project Investment Analysis</h3>
                        <div class="space-y-2">
                            <div class="flex justify-between"><span>Total Investment:</span><span>${formatCurrency(outputs.investmentPlan?.totalInvestment)}</span></div>
                            <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                            <div class="flex justify-between"><span>Project IRR (after tax):</span><span>${formatPercentage(outputs.indicators?.projectIRR)}</span></div>
                            <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                            <div class="flex justify-between"><span>Discounted Payback Period:</span><span>${formatYears(outputs.indicators?.discountedPaybackPeriod)}</span></div>
                        </div>
                    </div>
                    <div class="bg-white p-4 rounded-lg border border-gray-200">
                        <h3 class="font-semibold text-lg mb-3 text-green-700">Capital Analysis</h3>
                        <div class="space-y-2">
                            <div class="flex justify-between"><span>Capital NPV:</span><span>${formatCurrency(outputs.indicators?.capitalNPV)}</span></div>
                            <div class="flex justify-between"><span>Capital IRR (after tax):</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                        </div>
                    </div>
                </div>
                ${inputs.bessEnabled ? `
                <div class="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 class="font-semibold text-lg mb-3 text-purple-700 flex items-center">
                        🔋 BESS Analysis
                    </h3>
                    <div class="space-y-2">
                        <div class="flex justify-between"><span>BESS Investment:</span><span>${formatCurrency(outputs.investmentPlan?.bessInvestment)}</span></div>
                        <div class="flex justify-between"><span>BESS % of Total:</span><span>${formatPercentage((outputs.investmentPlan?.bessInvestment || 0) / (outputs.investmentPlan?.totalInvestment || 1))}</span></div>
                        <div class="flex justify-between"><span>Annual BESS Revenue:</span><span>${formatCurrency(outputs.bessStatement?.[0] ? (outputs.bessStatement[0].arbitrageRevenue + outputs.bessStatement[0].ancillaryRevenue + outputs.bessStatement[0].capacityRevenue + outputs.bessStatement[0].demandChargeReduction) : 0)}</span></div>
                        <div class="flex justify-between"><span>Total BESS Revenue:</span><span>${formatCurrency(outputs.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0)}</span></div>
                    </div>
                </div>
                ` : ''}
                <div class="bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
                    <h3 class="font-semibold text-lg mb-3 text-purple-700">Key Metrics</h3>
                    <div class="grid md:grid-cols-2 gap-x-6 gap-y-4">
                        <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                        <div class="flex justify-between"><span>Avg. Cost per kWh:</span><span>${formatCurrency(outputs.costStatement?.reduce((a,b) => a + b.costPerKwh, 0) / outputs.costStatement?.length, 4)}/kWh</span></div>
                        <div class="flex justify-between"><span>Total Net Profit:</span><span>${formatCurrency(outputs.pnlStatement?.reduce((a,b) => a + b.netProfit, 0))}</span></div>
                        <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0))}</span></div>
                    </div>
                </div>
                
                <div class="mt-8 kpi-section">
                    <h2 class="text-xl font-bold mb-4">Key Performance Indicators (KPIs)</h2>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <h3 class="text-lg font-semibold mb-3 text-blue-700">Investment Viability</h3>
                            <div class="space-y-2">
                                <div class="flex justify-between"><span>Project IRR vs Discount:</span><span>${formatPercentage(outputs.indicators?.projectIRR)} vs ${formatPercentage(inputs.discountRate)}</span></div>
                                <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                                <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                                <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                            </div>
                        </div>
                        <div>
                            <h3 class="text-lg font-semibold mb-3 text-green-700">Investor Returns</h3>
                            <div class="space-y-2">
                                <div class="flex justify-between"><span>Capital IRR:</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                                <div class="flex justify-between"><span>Capital NPV:</span><span>${formatCurrency(outputs.indicators?.capitalNPV)}</span></div>
                                <div class="flex justify-between"><span>Profit Distribution:</span><span>${formatPercentage(inputs.profitDistributionRate)}</span></div>
                                <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}</span></div>
                            </div>
                        </div>
                        <div>
                            <h3 class="text-lg font-semibold mb-3 text-purple-700">Financial Health</h3>
                            <div class="space-y-2">
                                <div class="flex justify-between"><span>Operating Margin:</span><span>${formatPercentage(1 - inputs.operatingCostRate)}</span></div>
                                <div class="flex justify-between"><span>Cost Escalation:</span><span>${formatPercentage(inputs.operatingCostEscalationRate)}</span></div>
                                <div class="flex justify-between"><span>Viability Status:</span><span>${(outputs.indicators?.projectIRR || 0) > inputs.discountRate ? 'VIABLE' : 'NOT VIABLE'}</span></div>
                                <div class="flex justify-between"><span>NPV Status:</span><span>${(outputs.indicators?.projectNPV || 0) > 0 ? 'POSITIVE' : 'NEGATIVE'}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
};

const generateBasicDataTabHtml = (inputs) => {
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - Basic Data</h1>
            <div class="grid lg:grid-cols-2 gap-8">
                <div class="bg-white p-6 rounded-lg border border-gray-200">
                    <h2 class="text-xl font-semibold mb-4 text-blue-700">Project Configuration</h2>
                    <div class="space-y-3">
                        <div class="flex justify-between"><span>Construction Period:</span><span>${inputs.constructionPeriod} years</span></div>
                        <div class="flex justify-between"><span>Operation Period:</span><span>${inputs.operationPeriod} years</span></div>
                        <div class="flex justify-between"><span>Installed Capacity:</span><span>${inputs.installedCapacity} MW</span></div>
                        <div class="flex justify-between"><span>Investment per MW:</span><span>${formatCurrency(inputs.investmentPerMW)}</span></div>
                        <div class="flex justify-between"><span>Own Funds:</span><span>${formatCurrency(inputs.ownFunds)}</span></div>
                        <div class="flex justify-between"><span>Hectares per MW:</span><span>${inputs.hectaresPerMW}</span></div>
                    </div>
                </div>
                <div class="bg-white p-6 rounded-lg border border-gray-200">
                    <h2 class="text-xl font-semibold mb-4 text-green-700">Revenue & Cost Parameters</h2>
                    <div class="space-y-3">
                        <div class="flex justify-between"><span>Annual Hours:</span><span>${inputs.annualHours} hours</span></div>
                        <div class="flex justify-between"><span>On-Grid Tariff:</span><span>${formatCurrency(inputs.onGridTariff, 4)}/kWh</span></div>
                        <div class="flex justify-between"><span>PV Degradation Rate:</span><span>${formatPercentage(inputs.pvDegradationRate)}</span></div>
                        <div class="flex justify-between"><span>Operating Cost Rate:</span><span>${formatPercentage(inputs.operatingCostRate)}</span></div>
                        <div class="flex justify-between"><span>Operating Cost Escalation:</span><span>${formatPercentage(inputs.operatingCostEscalationRate)}</span></div>
                        <div class="flex justify-between"><span>Land Cost per Hectare:</span><span>${formatCurrency(inputs.landCostPerHectare)}</span></div>
                    </div>
                </div>
            </div>
        </div>
    `;
};

const generateInvestmentFinancingTabHtml = (outputs) => {
    if (!outputs.investmentPlan) return generateDefaultTabHtml('Investment & Financing');
    
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - Investment & Financing</h1>
            <div class="space-y-6">
                <div>
                    <h2 class="text-xl font-semibold mb-4">Investment Plan</h2>
                    <div class="space-y-2">
                        <div class="flex justify-between"><span>Total Investment:</span><span>${formatCurrency(outputs.investmentPlan.totalInvestment)}</span></div>
                        <div class="flex justify-between"><span>Total Static Investment:</span><span>${formatCurrency(outputs.investmentPlan.totalStaticInvestment)}</span></div>
                        <div class="flex justify-between"><span>Working Capital:</span><span>${formatCurrency(outputs.investmentPlan.workingCapital)}</span></div>
                    </div>
                </div>
                <div>
                    <h2 class="text-xl font-semibold mb-4">Financing Structure</h2>
                    <div class="space-y-2">
                        <div class="flex justify-between"><span>Capital (Equity):</span><span>${formatCurrency(outputs.investmentPlan.financing.capital)}</span></div>
                        <div class="flex justify-between"><span>Loan:</span><span>${formatCurrency(outputs.investmentPlan.financing.loan)}</span></div>
                        <div class="flex justify-between"><span>Total Financing:</span><span>${formatCurrency(outputs.investmentPlan.financing.totalFinancing)}</span></div>
                    </div>
                </div>
            </div>
        </div>
    `;
};

const generateTableTabHtml = (outputs, tabName, dataKey, headers, rowGenerator) => {
    if (!outputs[dataKey] || outputs[dataKey].length === 0) {
        return generateDefaultTabHtml(tabName);
    }
    
    const tableRows = outputs[dataKey].slice(0, 15).map(rowGenerator).join('');
    const headerCells = headers.map(header => `<th class="border px-2 py-1">${header}</th>`).join('');
    
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tabName}</h1>
            <table class="w-full text-sm border-collapse border">
                <thead>
                    <tr class="bg-gray-100">${headerCells}</tr>
                </thead>
                <tbody>${tableRows}</tbody>
            </table>
        </div>
    `;
};

const generateLoanRepaymentTabHtml = (outputs) => {
    return generateTableTabHtml(
        outputs, 
        'Loan Repayment', 
        'loanRepayment',
        ['Year', 'Opening Balance', 'Interest', 'Principal', 'Closing Balance'],
        row => `
            <tr>
                <td class="border px-2 py-1">${row.year}</td>
                <td class="border px-2 py-1">${formatCurrency(row.openingBalance, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.interest, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.principal, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.closingBalance, 0)}</td>
            </tr>
        `
    );
};

const generateTotalCostTabHtml = (outputs) => {
    return generateTableTabHtml(
        outputs,
        'Total Cost',
        'costStatement',
        ['Year', 'Operating Cost', 'Land Cost', 'Depreciation', 'Cost per kWh'],
        row => `
            <tr>
                <td class="border px-2 py-1">${row.year}</td>
                <td class="border px-2 py-1">${formatCurrency(row.operatingCost, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.landCost, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.depreciation, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.costPerKwh, 4)}</td>
            </tr>
        `
    );
};

const generateProfitLossTabHtml = (outputs) => {
    return generateTableTabHtml(
        outputs,
        'Profit & Loss',
        'pnlStatement',
        ['Year', 'Operating Income', 'Operating Cost', 'Net Profit', 'Total Profit'],
        row => `
            <tr>
                <td class="border px-2 py-1">${row.year}</td>
                <td class="border px-2 py-1">${formatCurrency(row.operatingIncome, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.operatingCost, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.netProfit, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.totalProfit, 0)}</td>
            </tr>
        `
    );
};

const generateBalanceSheetTabHtml = (outputs) => {
    return generateTableTabHtml(
        outputs,
        'Balance Sheet',
        'balanceSheet',
        ['Year', 'Equity', 'Long Term Loan', 'Net Fixed Assets', 'Balance Check'],
        row => `
            <tr>
                <td class="border px-2 py-1">${row.year}</td>
                <td class="border px-2 py-1">${formatCurrency(row.equity, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.longTermLoan, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.netFixedAssets, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.check, 0)}</td>
            </tr>
        `
    );
};

const generateEVATabHtml = (outputs) => {
    return generateTableTabHtml(
        outputs,
        'EVA',
        'evaStatement',
        ['Year', 'NOPAT', 'Invested Capital', 'WACC', 'EVA'],
        row => `
            <tr>
                <td class="border px-2 py-1">${row.year}</td>
                <td class="border px-2 py-1">${formatCurrency(row.nopat, 0)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.investedCapital, 0)}</td>
                <td class="border px-2 py-1">${formatPercentage(row.wacc)}</td>
                <td class="border px-2 py-1">${formatCurrency(row.eva, 0)}</td>
            </tr>
        `
    );
};

const generateChartsTabHtml = (inputs, outputs) => {
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - Charts</h1>
            <p class="text-gray-600 mb-4">Financial charts visualization summary:</p>
            <div class="space-y-4">
                <div>
                    <h3 class="text-lg font-semibold mb-2">Profitability Trends</h3>
                    <p class="text-sm text-gray-600">Operating Income and Net Profit trends over the project lifecycle.</p>
                </div>
                <div>
                    <h3 class="text-lg font-semibold mb-2">Cost Structure Analysis</h3>
                    <p class="text-sm text-gray-600">Breakdown of operating costs, land costs, depreciation, and interest over time.</p>
                </div>
                <div class="mt-6">
                    <h3 class="text-lg font-semibold mb-2">Key Financial Indicators Summary</h3>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div class="space-y-2">
                            <div class="flex justify-between"><span>Project NPV:</span><span>${formatCurrency(outputs.indicators?.projectNPV)}</span></div>
                            <div class="flex justify-between"><span>Project IRR:</span><span>${formatPercentage(outputs.indicators?.projectIRR)}</span></div>
                            <div class="flex justify-between"><span>Capital IRR:</span><span>${formatPercentage(outputs.indicators?.capitalIRR)}</span></div>
                        </div>
                        <div class="space-y-2">
                            <div class="flex justify-between"><span>LCOE:</span><span>${formatCurrency(outputs.indicators?.lcoe, 4)}/kWh</span></div>
                            <div class="flex justify-between"><span>Payback Period:</span><span>${formatYears(outputs.indicators?.paybackPeriod)}</span></div>
                            <div class="flex justify-between"><span>Total EVA:</span><span>${formatCurrency(outputs.evaStatement?.reduce((a,b) => a + b.eva, 0) || 0)}</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
};

const generateDefaultTabHtml = (tab) => {
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
            <p class="text-gray-600">Content for ${tab} tab - refer to the digital version for full interactive content.</p>
        </div>
    `;
};

const generateErrorTabHtml = (tab) => {
    return `
        <div class="print-page">
            <h1 class="text-3xl font-bold mb-6 text-gray-800">Solar Financial Model - ${tab}</h1>
            <p class="text-gray-600">Error loading content for ${tab}</p>
        </div>
    `;
};

export const printReport = async (inputs, outputs, tabs) => {
    const allTabsContent = await generatePrintableReport(inputs, outputs, tabs);
    
    // Create print frame
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    
    document.body.appendChild(printFrame);
    
    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(getPrintStylesHtml(allTabsContent));
    frameDoc.close();

    setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => {
            document.body.removeChild(printFrame);
        }, 100);
    }, 500);
};

const getPrintStylesHtml = (content) => {
    return `
        <html>
            <head>
                <title>Solar Financial Model - Complete Report</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #333; }
                    .print-page { page-break-after: always; padding: 20px; }
                    .print-page:last-child { page-break-after: auto; }
                    h1 { font-size: 24px; margin-bottom: 20px; color: #1f2937; }
                    h2 { font-size: 18px; margin-bottom: 15px; color: #374151; }
                    h3 { font-size: 16px; margin-bottom: 10px; color: #4b5563; }
                    table { border-collapse: collapse; width: 100%; margin: 15px 0; }
                    th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
                    th { background-color: #f3f4f6; font-weight: bold; }
                    .space-y-2 > * + * { margin-top: 8px; }
                    .space-y-3 > * + * { margin-top: 12px; }
                    .space-y-6 > * + * { margin-top: 24px; }
                    .grid { display: grid; gap: 20px; }
                    .md\\:grid-cols-2 { grid-template-columns: 1fr 1fr; }
                    .bg-white { background-color: white; }
                    .border { border: 1px solid #d1d5db; }
                    .rounded-lg { border-radius: 8px; }
                    .p-4 { padding: 16px; }
                    .p-6 { padding: 24px; }
                    .flex { display: flex; }
                    .justify-between { justify-content: space-between; }
                    .font-semibold { font-weight: 600; }
                    .text-blue-700 { color: #1d4ed8; }
                    .text-green-700 { color: #047857; }
                    .text-purple-700 { color: #7c3aed; }
                    .text-gray-600 { color: #4b5563; }
                    .text-gray-800 { color: #1f2937; }
                    @page { margin: 1in; size: letter; }
                    @media print {
                        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        .print-page { page-break-after: always; }
                        .print-page:last-child { page-break-after: auto; }
                    }
                    
                    /* Summary Tab Print Optimization */
                    .summary-print-container {
                        font-size: 13px !important;
                        line-height: 1.4 !important;
                        margin: 0 !important;
                        padding: 12px !important;
                    }
                    .summary-print-container h2 {
                        font-size: 20px !important;
                        margin-bottom: 12px !important;
                        font-weight: bold !important;
                    }
                    .summary-print-container h3 {
                        font-size: 16px !important;
                        margin-bottom: 8px !important;
                        font-weight: 600 !important;
                    }
                    .summary-print-container h4 {
                        font-size: 13px !important;
                        margin-bottom: 6px !important;
                        font-weight: 600 !important;
                    }
                    .summary-print-container .grid {
                        gap: 12px !important;
                    }
                    .summary-print-container .p-4 {
                        padding: 10px !important;
                    }
                    .summary-print-container .p-6 {
                        padding: 12px !important;
                    }
                    .summary-print-container .mb-3 {
                        margin-bottom: 6px !important;
                    }
                    .summary-print-container .mb-4 {
                        margin-bottom: 8px !important;
                    }
                    .summary-print-container .mb-6 {
                        margin-bottom: 10px !important;
                    }
                    .summary-print-container .mt-8 {
                        margin-top: 16px !important;
                    }
                    .summary-print-container .space-y-4 > * + * {
                        margin-top: 8px !important;
                    }
                    .summary-print-container .text-lg {
                        font-size: 15px !important;
                    }
                    .summary-print-container .text-xl {
                        font-size: 16px !important;
                    }
                    .summary-print-container .text-2xl {
                        font-size: 20px !important;
                    }
                    .summary-print-container .text-sm {
                        font-size: 11px !important;
                    }
                    .summary-print-container .border {
                        border-width: 1px !important;
                    }
                    .summary-print-container .rounded-lg {
                        border-radius: 4px !important;
                    }
                    .summary-print-container .w-5 {
                        width: 16px !important;
                        height: 16px !important;
                    }
                    .summary-print-container .kpi-section {
                        page-break-before: always !important;
                    }
                    
                    /* Preserve Grid Layouts for Print */
                    .summary-print-container .grid.md\\:grid-cols-2 {
                        display: grid !important;
                        grid-template-columns: 1fr 1fr !important;
                    }
                    .summary-print-container .grid.grid-cols-1.md\\:grid-cols-3 {
                        display: grid !important;
                        grid-template-columns: 1fr 1fr 1fr !important;
                    }
                    .summary-print-container .grid.md\\:grid-cols-2.gap-x-6 {
                        display: grid !important;
                        grid-template-columns: 1fr 1fr !important;
                    }
                    .summary-print-container .md\\:col-span-2 {
                        grid-column: span 2 !important;
                    }
                </style>
            </head>
            <body>
                ${content}
            </body>
        </html>
    `;
}; 